// TODO: Split InputStepper to InputNumber component
import {
  CSSProperties,
  ChangeEvent,
  FC,
  InputHTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { InputStepContainer, StepButton, StepInput } from './styled';

interface StepProp {
  up: number;
  down: number;
}
interface InputStepperProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'onChange' | 'min' | 'max' | 'value' | 'step'
  > {
  containerStyle?: CSSProperties;
  min?: number;
  max?: number;
  value?: number;
  step?: number | StepProp;
  disableDecimal?: boolean;
  onChange?(value: number): void;
}

const InputStepper: FC<InputStepperProps> = ({
  containerStyle,
  onChange = () => {},
  step = 1,
  disableDecimal = false,
  ...props
}) => {
  const [inputValue, setInputValue] = useState(
    props.value?.toString() || props.defaultValue?.toString() || '0',
  );

  const { minValue, maxValue } = useMemo(() => {
    const { min, max } = props;

    return {
      minValue: min === undefined ? -Infinity : Number(min),
      maxValue: max === undefined ? Infinity : Number(max),
    };
  }, [props.min, props.max]);

  const formatter = (value: number) => Intl.NumberFormat('en-US').format(value);

  const parser = (value: string) => {
    // Purposefully hardcoded number only to get separator symbol
    const thousandSeparator = Intl.NumberFormat('en-US')
      .format(1111)
      .replace(/[0-9]+/g, '');
    const decimalSeparator = Intl.NumberFormat('en-US')
      .format(1.1)
      .replace(/[0-9]+/g, '');
    const [wholeValue, decimalValue = 0] = value
      .replaceAll(thousandSeparator, '')
      .split(decimalSeparator);

    return Number(`${wholeValue}${decimalSeparator}${decimalValue}`);
  };

  const parsedInputValue = useMemo(() => parser(inputValue), [inputValue]);

  const setTextCursor = (
    target: EventTarget & HTMLInputElement,
    position: number,
  ) => {
    window.requestAnimationFrame(() => {
      target.focus();
      target.setSelectionRange(position, position);
    });
  };

  const getStep = (key: keyof StepProp) => step[key] || step;

  // Clamp value optionally, set to Infinity to handle unclamped value
  const getClampedValue = (value: number) =>
    Math.min(Math.max(value, minValue), maxValue);

  const getProcessedValue = (value: string) => {
    const parsedValue = getClampedValue(parser(value));
    return {
      rawValue: parsedValue,
      formattedValue: formatter(parsedValue),
    };
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const isReplaceZero = parsedInputValue === 0;
    const { rawValue, formattedValue } = getProcessedValue(
      isReplaceZero ? value.replace('0', '') : value,
    );
    const selectionPos = e.target.selectionStart;

    // Handle on value not convertible to number
    if (Number.isNaN(rawValue)) return;

    // Handle when inputing decimal
    if (
      value[value.length - 1] === '.' &&
      !inputValue.includes('.') &&
      !disableDecimal
    ) {
      setInputValue(`${formattedValue}.`);
      return;
    }

    // Handle on removing comma
    if (formattedValue === inputValue) {
      // Check if the last character removed was '0'
      if (inputValue === '0' && selectionPos === 0) {
        // Set the cursor to the right of '0'
        setTextCursor(e.target, selectionPos + 1);
        return;
      }

      setTextCursor(e.target, selectionPos);
      return;
    }

    setInputValue(formattedValue);
    onChange(rawValue);

    // Prevent cursor jumping when adding and removing text
    const shiftPos = inputValue.length - value.length;
    const newCursor =
      selectionPos + (formattedValue.length - inputValue.length + shiftPos);
    setTextCursor(e.target, newCursor);
  };

  const handleStepDown = () => {
    const stepValue = getStep('down');
    const rawValue = getClampedValue(parsedInputValue - stepValue);
    setInputValue(formatter(rawValue));
    onChange(rawValue);
  };

  const handleStepUp = () => {
    const stepValue = getStep('up');
    const rawValue = getClampedValue(parsedInputValue + stepValue);
    setInputValue(formatter(rawValue));
    onChange(rawValue);
  };

  const handlePropValueChange = useCallback(() => {
    if (props.value === undefined) return;

    const newValue = formatter(getClampedValue(props.value || 0));
    setInputValue(newValue);
  }, [props.value]);

  useEffect(() => {
    handlePropValueChange();
  }, [handlePropValueChange]);

  return (
    <InputStepContainer style={containerStyle}>
      <StepButton
        type="button"
        data-cy="btn-step-down"
        onClick={handleStepDown}
        disabled={parsedInputValue <= minValue || props.disabled}
      >
        -
      </StepButton>
      <StepInput
        data-cy="input-step"
        {...props}
        onChange={handleChange}
        value={inputValue}
      />
      <StepButton
        type="button"
        data-cy="btn-step-up"
        onClick={handleStepUp}
        disabled={parsedInputValue >= maxValue || props.disabled}
      >
        +
      </StepButton>
    </InputStepContainer>
  );
};

export default InputStepper;
